import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookOpen,
  faPlus,
  faEdit,
  faTrash,
  faCogs,
  faExternalLinkAlt,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { streetlearnApi } from '../api';
import { showToast } from '../../components/toast';

const StreetlearnCurriculum = () => {
  const [levels, setLevels] = useState([]);
  const [selectedLevelId, setSelectedLevelId] = useState(1);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [lessonTypes, setLessonTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals state
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [moduleForm, setModuleForm] = useState({ code: '', title: '', sort_order: 1 });

  const [showLessonModal, setShowLessonModal] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [lessonForm, setLessonForm] = useState({
    code: '',
    module_id: '',
    title: '',
    brief: '',
    type_code: 'GEN',
    duration_min: 15,
    is_free: false,
    status: 'draft',
  });

  const [showLevelModal, setShowLevelModal] = useState(false);
  const [levelForm, setLevelForm] = useState({
    title: '',
    tagline: '',
    hours: '',
    prereq: '',
    presenter: '',
    status: 'published',
  });

  const [saving, setSaving] = useState(false);

  const loadLevels = async () => {
    try {
      const lvls = await streetlearnApi.getLevels();
      setLevels(lvls || []);
      if (lvls && lvls.length > 0 && !selectedLevelId) {
        setSelectedLevelId(lvls[0].id);
      }
    } catch (err) {
      console.error('Failed to load levels:', err);
    }
  };

  const loadLevelContent = async (lvlId) => {
    try {
      setLoading(true);
      const [mods, les, types] = await Promise.all([
        streetlearnApi.getModules(lvlId),
        streetlearnApi.getLessons({ level_id: lvlId }),
        streetlearnApi.getLessonTypes(),
      ]);
      setModules(mods || []);
      setLessons(les || []);
      setLessonTypes(types || []);
    } catch (err) {
      console.error('Failed to load level content:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLevels();
  }, []);

  useEffect(() => {
    if (selectedLevelId) {
      loadLevelContent(selectedLevelId);
    }
  }, [selectedLevelId]);

  const selectedLevel = levels.find((l) => l.id === Number(selectedLevelId));

  // Module handlers
  const handleOpenModuleModal = (mod = null) => {
    if (mod) {
      setEditingModule(mod);
      setModuleForm({ code: mod.code, title: mod.title, sort_order: mod.sort_order });
    } else {
      setEditingModule(null);
      setModuleForm({
        code: `M${modules.length + 1}`,
        title: '',
        sort_order: modules.length + 1,
      });
    }
    setShowModuleModal(true);
  };

  const handleSaveModule = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (editingModule) {
        await streetlearnApi.updateModule(editingModule.id, moduleForm);
      } else {
        await streetlearnApi.createModule({
          level_id: Number(selectedLevelId),
          ...moduleForm,
        });
      }
      setShowModuleModal(false);
      showToast.success(editingModule ? 'Module updated successfully!' : 'Module created successfully!');
      loadLevelContent(selectedLevelId);
    } catch (err) {
      showToast.error(err?.response?.data?.message || 'Failed to save module.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModule = async (modId) => {
    if (!window.confirm('Are you sure you want to delete this module?')) return;
    try {
      await streetlearnApi.deleteModule(modId);
      showToast.success('Module deleted successfully!');
      loadLevelContent(selectedLevelId);
    } catch (err) {
      showToast.error(err?.response?.data?.message || 'Failed to delete module.');
    }
  };

  // Lesson handlers
  const handleOpenLessonModal = (les = null, defaultModId = null) => {
    if (les) {
      const defaultType = lessonTypes.length > 0 ? lessonTypes[0].code : 'GEN';
      setEditingLesson(les);
      setLessonForm({
        code: les.code,
        module_id: les.module_id,
        title: les.title,
        brief: les.brief || '',
        type_code: les.type_code || defaultType,
        duration_min: les.duration_min || 15,
        is_free: Boolean(les.is_free),
        status: les.status || 'published',
      });
    } else {
      setEditingLesson(null);
      const modId = defaultModId || (modules[0] ? modules[0].id : '');
      const nextNum = lessons.length + 1;
      const code = `L${selectedLevel?.number || 1}.${nextNum}`;
      const defaultType = lessonTypes.length > 0 ? lessonTypes[0].code : 'GEN';
      setLessonForm({
        code,
        module_id: modId,
        title: '',
        brief: '',
        type_code: defaultType,
        duration_min: 15,
        is_free: false,
        status: 'published',
      });
    }
    setShowLessonModal(true);
  };

  const handleSaveLesson = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (editingLesson) {
        await streetlearnApi.updateLesson(editingLesson.id, lessonForm);
        showToast.success('Lesson updated successfully!');
      } else {
        await streetlearnApi.createLesson({
          level_id: Number(selectedLevelId),
          ...lessonForm,
          module_id: Number(lessonForm.module_id),
        });
        showToast.success('Lesson created successfully!');
      }
      setShowLessonModal(false);
      loadLevelContent(selectedLevelId);
    } catch (err) {
      showToast.error(err?.response?.data?.message || 'Failed to save lesson.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLesson = async (lesId) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) return;
    try {
      await streetlearnApi.deleteLesson(lesId);
      showToast.success('Lesson deleted successfully!');
      loadLevelContent(selectedLevelId);
    } catch (err) {
      showToast.error(err?.response?.data?.message || 'Failed to delete lesson.');
    }
  };

  // Level Settings handler
  const handleOpenLevelModal = () => {
    if (!selectedLevel) return;
    setLevelForm({
      title: selectedLevel.title || '',
      tagline: selectedLevel.tagline || '',
      hours: selectedLevel.hours || '',
      prereq: selectedLevel.prereq || '',
      presenter: selectedLevel.presenter || '',
      status: selectedLevel.status || 'published',
    });
    setShowLevelModal(true);
  };

  const handleSaveLevel = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await streetlearnApi.updateLevel(selectedLevelId, levelForm);
      showToast.success('Level settings updated successfully!');
      setShowLevelModal(false);
      loadLevels();
    } catch (err) {
      showToast.error(err?.response?.data?.message || 'Failed to update level.');
    } finally {
      setSaving(false);
    }
  };

  // Group lessons by module
  const lessonsByModule = new Map();
  lessons.forEach((l) => {
    const list = lessonsByModule.get(l.module_id) || [];
    list.push(l);
    lessonsByModule.set(l.module_id, list);
  });

  return (
    <div className="mt-5">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <div>
          <h4 className="panel-title m-0">
            <FontAwesomeIcon icon={faBookOpen} className="me-2 text-primary" />
            StreetLearn Curriculum &amp; Modules Management
          </h4>
        </div>
        <div className="d-flex align-items-center flex-wrap">
          <button
            className="btn btn-default btn-sm mx-1"
            onClick={() => loadLevelContent(selectedLevelId)}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
          </button>
          <button className="btn btn-default btn-sm mx-1" onClick={handleOpenLevelModal}>
            <FontAwesomeIcon icon={faCogs} className="me-1" /> Edit Level Settings
          </button>
          <button className="btn btn-primary btn-sm mx-1" onClick={() => handleOpenModuleModal()}>
            <FontAwesomeIcon icon={faPlus} className="me-1" /> Add Module
          </button>
        </div>
      </div>

      {/* Level Selector Tabs */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-body py-2">
          <div className="d-flex flex-wrap align-items-center" style={{ gap: '5px' }}>
            {levels.map((lvl) => (
              <button
                key={lvl.id}
                type="button"
                className={`btn btn-xs ${
                  selectedLevelId === lvl.id ? 'btn-primary active font-bold' : 'btn-default'
                }`}
                style={{
                  flex: '0 0 auto',
                  padding: '4px 10px',
                  borderRadius: '3px',
                  fontSize: '11px',
                }}
                onClick={() => setSelectedLevelId(lvl.id)}
              >
                Level {lvl.number} ({lvl.lesson_count || 0})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Level Summary Banner */}
      {selectedLevel && (
        <div className="panel panel-default with-rounded-corner with-shadow mb-3">
          <div className="panel-body d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <span className="badge badge-primary me-2">
                {selectedLevel.tier?.name || 'Foundation'} Tier
              </span>
              <h4 className="d-inline font-bold text-dark align-middle m-0">
                Level {selectedLevel.number}: {selectedLevel.title}
              </h4>
              <p className="text-muted small mb-0 mt-1">
                {selectedLevel.tagline || 'Master financial fundamentals, technical analysis, and market principles.'}
              </p>
            </div>
            <div className="d-flex gap-4 text-center">
              <div>
                <small className="text-muted d-block">Modules</small>
                <strong className="text-dark font-bold">{modules.length}</strong>
              </div>
              <div>
                <small className="text-muted d-block">Lessons</small>
                <strong className="text-dark font-bold">{lessons.length}</strong>
              </div>
              <div>
                <small className="text-muted d-block">Est. Hours</small>
                <strong className="text-dark font-bold">{selectedLevel.hours || 'N/A'}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modules & Lessons List */}
      {loading ? (
        <div className="text-center py-5">
          <FontAwesomeIcon icon={faRefresh} spin size="2x" className="text-primary mb-2" />
          <p className="text-muted">Loading modules and lessons...</p>
        </div>
      ) : modules.length === 0 ? (
        <div className="panel panel-default with-rounded-corner with-shadow p-5 text-center">
          <p className="text-muted mb-3">No modules configured for this level yet.</p>
          <button className="btn btn-primary btn-sm" onClick={() => handleOpenModuleModal()}>
            <FontAwesomeIcon icon={faPlus} className="me-1" /> Add First Module
          </button>
        </div>
      ) : (
        modules.map((mod) => {
          const modLessons = lessonsByModule.get(mod.id) || [];

          return (
            <div className="panel panel-default with-rounded-corner with-shadow mb-3" key={mod.id}>
              <div className="panel-heading border-0 d-flex justify-content-between align-items-center">
                <div>
                  <span className="badge badge-inverse me-2">{mod.code}</span>
                  <strong className="text-dark" style={{ fontSize: '15px' }}>
                    {mod.title}
                  </strong>
                  <span className="text-muted small ms-2">({modLessons.length} lessons)</span>
                </div>
                <div>
                  <button
                    className="btn btn-primary btn-xs mx-1"
                    onClick={() => handleOpenLessonModal(null, mod.id)}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" /> Add Lesson
                  </button>
                  <button
                    className="btn btn-default btn-xs mx-1"
                    onClick={() => handleOpenModuleModal(mod)}
                  >
                    <FontAwesomeIcon icon={faEdit} className="me-1" /> Edit
                  </button>
                  <button
                    className="btn btn-danger btn-xs mx-1"
                    onClick={() => handleDeleteModule(mod.id)}
                    disabled={modLessons.length > 0}
                    title={modLessons.length > 0 ? 'Cannot delete module containing lessons' : ''}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>

              <div className="panel-body p-0">
                {modLessons.length === 0 ? (
                  <div className="text-center py-3 text-muted small">
                    No lessons in this module yet. Click "+ Add Lesson" to create one.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-condensed table-hover bg-white m-0">
                      <thead>
                        <tr>
                          <th style={{ width: '80px' }}>Code</th>
                          <th>Lesson Title &amp; Synopsis</th>
                          <th style={{ minWidth: '90px' }}>Type</th>
                          <th style={{ minWidth: '80px' }}>Duration</th>
                          <th style={{ minWidth: '100px' }}>Access</th>
                          <th style={{ minWidth: '100px' }}>Status</th>
                          <th style={{ minWidth: '100px' }}>Quiz Pool</th>
                          <th style={{ width: '160px', textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modLessons.map((les) => (
                          <tr key={les.id}>
                            <td>
                              <strong>{les.code}</strong>
                            </td>
                            <td>
                              <div className="font-bold text-dark">{les.title}</div>
                              {les.brief && (
                                <small className="text-muted text-truncate d-block" style={{ maxWidth: '380px' }}>
                                  {les.brief}
                                </small>
                              )}
                            </td>
                            <td>
                              <span className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: 600 }}>
                                {les.type_code}
                              </span>
                            </td>
                            <td>{les.duration_min ? `${les.duration_min} min` : '15 min'}</td>
                            <td>
                              {les.is_free ? (
                                <span className="text-success font-weight-bold">Free Preview</span>
                              ) : (
                                <span className="text-muted">Paid Only</span>
                              )}
                            </td>
                            <td>
                              {les.status === 'published' ? (
                                <span className="text-success font-weight-bold">Published</span>
                              ) : les.status === 'in_review' ? (
                                <span className="text-warning font-weight-bold">In Review</span>
                              ) : (
                                <span className="text-muted">Draft</span>
                              )}
                            </td>
                            <td>
                              <span className="text-info font-weight-bold">
                                {les.question_count || 0} Quiz Qs
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <Link
                                to={`/streetlearn/curriculum/lessons/${les.id}/studio`}
                                className="btn btn-primary btn-xs mx-1"
                                title="Open Studio"
                              >
                                <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" /> Studio
                              </Link>
                              <button
                                className="btn btn-default btn-xs mx-1"
                                onClick={() => handleOpenLessonModal(les)}
                                title="Edit Lesson"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                className="btn btn-danger btn-xs mx-1"
                                onClick={() => handleDeleteLesson(les.id)}
                                title="Delete Lesson"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* Module Modal */}
      {showModuleModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleSaveModule}>
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h4 className="modal-title font-bold m-0">
                    {editingModule ? 'Edit Module' : 'Add New Module'}
                  </h4>
                  <button
                    type="button"
                    className="close"
                    style={{ border: 'none', background: 'transparent', fontSize: '24px', lineHeight: '1', cursor: 'pointer', padding: '0 5px' }}
                    onClick={() => setShowModuleModal(false)}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group mb-3">
                    <label className="font-semibold text-sm">
                      Module Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={moduleForm.code}
                      onChange={(e) => setModuleForm({ ...moduleForm, code: e.target.value })}
                      placeholder="e.g. M1, M2"
                      required
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="font-semibold text-sm">
                      Module Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={moduleForm.title}
                      onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                      placeholder="e.g. Fundamental Analysis Basics"
                      required
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="font-semibold text-sm">Sort Order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={moduleForm.sort_order}
                      onChange={(e) =>
                        setModuleForm({ ...moduleForm, sort_order: Number(e.target.value) })
                      }
                      min="1"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => setShowModuleModal(false)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Module'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Lesson Modal */}
      {showLessonModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <form onSubmit={handleSaveLesson}>
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h4 className="modal-title font-bold m-0">
                    {editingLesson ? 'Edit Lesson' : 'Add New Lesson'}
                  </h4>
                  <button
                    type="button"
                    className="close"
                    style={{ border: 'none', background: 'transparent', fontSize: '24px', lineHeight: '1', cursor: 'pointer', padding: '0 5px' }}
                    onClick={() => setShowLessonModal(false)}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="font-semibold text-sm">
                        Lesson Code <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={lessonForm.code}
                        onChange={(e) => setLessonForm({ ...lessonForm, code: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-8">
                      <label className="font-semibold text-sm">
                        Belongs to Module <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        value={lessonForm.module_id}
                        onChange={(e) =>
                          setLessonForm({ ...lessonForm, module_id: e.target.value })
                        }
                        required
                      >
                        {modules.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.code} — {m.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group mb-3">
                    <label className="font-semibold text-sm">
                      Lesson Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={lessonForm.title}
                      onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group mb-3">
                    <label className="font-semibold text-sm">Brief Synopsis</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={lessonForm.brief}
                      onChange={(e) => setLessonForm({ ...lessonForm, brief: e.target.value })}
                      placeholder="One or two sentences summarizing what learners will acquire..."
                    />
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="font-semibold text-sm">Lesson Type</label>
                      <select
                        className="form-control"
                        value={lessonForm.type_code}
                        onChange={(e) =>
                          setLessonForm({ ...lessonForm, type_code: e.target.value })
                        }
                      >
                        {lessonTypes.length > 0 ? (
                          lessonTypes.map((t) => (
                            <option key={t.code} value={t.code}>
                              {t.name} ({t.code})
                            </option>
                          ))
                        ) : (
                          <>
                            <option value="GEN">General (GEN)</option>
                            <option value="TA">Technical Analysis (TA)</option>
                            <option value="FA">Fundamental Analysis (FA)</option>
                            <option value="DERIV">Derivatives (DERIV)</option>
                            <option value="PSY">Psychology (PSY)</option>
                            <option value="PHIL">Philosophy (PHIL)</option>
                            <option value="REG">Regulation (REG)</option>
                            <option value="SYS">Systems (SYS)</option>
                          </>
                        )}
                      </select>
                    </div>

                    <div className="col-md-4">
                      <label className="font-semibold text-sm">Duration (Minutes)</label>
                      <input
                        type="number"
                        className="form-control"
                        min="1"
                        max="180"
                        value={lessonForm.duration_min}
                        onChange={(e) =>
                          setLessonForm({ ...lessonForm, duration_min: Number(e.target.value) })
                        }
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="font-semibold text-sm">Publish Status</label>
                      <select
                        className="form-control"
                        value={lessonForm.status}
                        onChange={(e) => setLessonForm({ ...lessonForm, status: e.target.value })}
                      >
                        <option value="published">Published</option>
                        <option value="in_review">In Review</option>
                        <option value="draft">Draft</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="isFreePreview"
                      checked={lessonForm.is_free}
                      onChange={(e) => setLessonForm({ ...lessonForm, is_free: e.target.checked })}
                    />
                    <label className="form-check-label font-semibold text-sm" htmlFor="isFreePreview">
                      Enable Free Preview (Open to non-subscribers)
                    </label>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => setShowLessonModal(false)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Lesson'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Level Settings Modal */}
      {showLevelModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleSaveLevel}>
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h4 className="modal-title font-bold m-0">
                    Edit Level {selectedLevel?.number} Settings
                  </h4>
                  <button
                    type="button"
                    className="close"
                    style={{ border: 'none', background: 'transparent', fontSize: '24px', lineHeight: '1', cursor: 'pointer', padding: '0 5px' }}
                    onClick={() => setShowLevelModal(false)}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group mb-3">
                    <label className="font-semibold text-sm">Level Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={levelForm.title}
                      onChange={(e) => setLevelForm({ ...levelForm, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="font-semibold text-sm">Tagline / Summary</label>
                    <input
                      type="text"
                      className="form-control"
                      value={levelForm.tagline}
                      onChange={(e) => setLevelForm({ ...levelForm, tagline: e.target.value })}
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="font-semibold text-sm">Estimated Study Hours</label>
                    <input
                      type="text"
                      className="form-control"
                      value={levelForm.hours}
                      onChange={(e) => setLevelForm({ ...levelForm, hours: e.target.value })}
                      placeholder="e.g. 8-10 Hours"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => setShowLevelModal(false)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Level Settings'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreetlearnCurriculum;
